import React, { useState, useEffect } from 'react';
import { Box, Paper, Typography, TextField, Button, MenuItem, RadioGroup, FormControlLabel, Radio, CircularProgress, FormLabel, Chip, Checkbox } from '@mui/material';
import api from '../../api';

const SwipeRequestSection = ({ empId, showMsg, onSuccess }) => {
  const [date, setDate] = useState('');
  const [reason, setReason] = useState('');
  const [otherReason, setOtherReason] = useState('');
  const [inTime, setInTime] = useState('');
  const [outTime, setOutTime] = useState('');
  const [file, setFile] = useState(null);
  const [isNextDay, setIsNextDay] = useState(false); // NEW STATE for Night Shift
  
  const [biometricStatus, setBiometricStatus] = useState('idle'); 
  const [biometricLogs, setBiometricLogs] = useState({ in: '', out: '' });
  const [biometricUse, setBiometricUse] = useState('no');

  const [submitting, setSubmitting] = useState(false);

  const [usage, setUsage] = useState({});
  const [limits, setLimits] = useState({});

  const reasons = ["Biometric", "WFH", "Tour", "Training", "Meeting", "Other"];

  // Fetch limits dynamically based on the selected date
  const fetchLimits = async (selectedDate) => {
    // If no date is selected yet, check today's limits
    const queryDate = selectedDate || new Date().toISOString().split('T')[0];
    try {
      const res = await api.get(`/swipe-request/limits/${empId}?target_date=${queryDate}`);
      setUsage(res.data.usage);
      setLimits(res.data.limits);
    } catch (err) {
      console.error("Failed to fetch limits", err);
    }
  };

  // Re-run the limit check anytime the date changes
  useEffect(() => {
    if (empId) fetchLimits(date);
  }, [empId, date]);

  // Fetch Biometric Data
  useEffect(() => {
    if (reason === 'Biometric' && date) {
      setBiometricStatus('loading');
      setBiometricUse('no');
      // Pass is_next_day to shift the biometric search window
      api.get(`/swipe-request/biometric-log/${empId}?date=${date}&is_next_day=${isNextDay}`)
        .then(res => {
          if (res.data.in_time && res.data.out_time) {
            setBiometricLogs({ in: res.data.in_time, out: res.data.out_time });
            setBiometricStatus('success');
            setInTime(res.data.in_time);
            setOutTime(res.data.out_time);
          } else {
            setBiometricStatus('empty');
          }
        })
        .catch(err => {
          console.error(err);
          setBiometricStatus('error');
        });
    }
  }, [reason, date, empId, isNextDay]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (reason === 'Biometric' && biometricUse !== 'yes') {
      return showMsg("You must agree to use the biometric time.", "error");
    }
    if (['WFH', 'Tour', 'Training', 'Meeting', 'Other'].includes(reason)) {
      if (!file) return showMsg("Proof document is mandatory.", "error");
      if (!inTime || !outTime) return showMsg("Check In and Check Out times are required.", "error");
    }
    if (reason === 'Other' && !otherReason.trim()) {
      return showMsg("Please provide a reason.", "error");
    }
    
    // Strict Validation for Out Time earlier than In Time without the checkbox
    if (!isNextDay && outTime <= inTime) {
       return showMsg("Check-Out time cannot be earlier than Check-In time unless it's the next day.", "error");
    }

    setSubmitting(true);
    const formData = new FormData();
    formData.append('date_str', date);
    formData.append('reason', reason);
    formData.append('in_time', inTime);
    formData.append('out_time', outTime);
    formData.append('is_next_day', isNextDay); // Send flag to backend
    if (reason === 'Other') formData.append('other_reason', otherReason);
    if (file) formData.append('file', file);

    try {
      await api.post(`/swipe-request/submit/${empId}`, formData, { 
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      showMsg("Swipe Request submitted successfully!");
      fetchLimits(date); // Refresh the limits for this specific month
      onSuccess(); 
    } catch (err) {
      showMsg(err.response?.data?.detail || "Failed to submit request.", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const currentUsage = usage[reason] || 0;
  const maxLimit = limits[reason] || 10;
  const limitReached = reason && currentUsage >= maxLimit;

  return (
    <Paper elevation={2} sx={{ p: 4, maxWidth: 550, borderRadius: 2 }}>
      <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        
        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField 
            size="small" type="date" label="Date" required 
            InputLabelProps={{ shrink: true }} 
            value={date} onChange={e => setDate(e.target.value)} 
            sx={{ flex: 1 }}
          />
          <TextField 
            select size="small" label="Reason" required 
            value={reason} onChange={e => setReason(e.target.value)}
            sx={{ flex: 1 }}
          >
            {reasons.map(r => <MenuItem key={r} value={r}>{r}</MenuItem>)}
          </TextField>
        </Box>

        {/* VISUAL BADGE FOR LIMITS */}
        {reason && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: -2 }}>
            <Chip 
              size="small" 
              label={`Monthly Usage: ${currentUsage} / ${maxLimit} ${reason}`} 
              color={limitReached ? "error" : "primary"} 
              variant={limitReached ? "filled" : "outlined"} 
              sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}
            />
          </Box>
        )}

        {limitReached && (
          <Typography color="error" variant="body2" sx={{ fontWeight: 'bold', textAlign: 'center', mt: -1 }}>
            You have exceeded the {maxLimit} request limit for {reason} this month. Please contact HR.
          </Typography>
        )}

        {/* NIGHT SHIFT CHECKBOX (Visible for all valid reasons) */}
        {!limitReached && reason && (
          <FormControlLabel
            control={<Checkbox checked={isNextDay} onChange={(e) => setIsNextDay(e.target.checked)} size="small" />}
            label={<Typography variant="body2" sx={{ fontWeight: 'bold', color: 'primary.main' }}>Check-out is on the next day (Night Shift)</Typography>}
            sx={{ mt: -1, mb: reason === 'Biometric' ? -1 : 0 }}
          />
        )}

        {/* BIOMETRIC FLOW */}
        {!limitReached && reason === 'Biometric' && date && (
          <Box sx={{ p: 2, bgcolor: '#fbfbfb', borderRadius: 2, border: '1px solid #e0e0e0' }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>Biometric DB Logs for {date}</Typography>
            
            {biometricStatus === 'loading' && <CircularProgress size={24} />}
            {biometricStatus === 'error' && <Typography color="error" variant="body2">Failed to connect to Biometric database.</Typography>}
            {biometricStatus === 'empty' && (
              <Typography color="error" variant="body2" sx={{ fontWeight: 'bold', mt: 1 }}>
                No records found, please check with HR.
              </Typography>
            )}
            
            {biometricStatus === 'success' && (
              <>
                <Typography variant="body2" sx={{ mb: 2, color: 'primary.main', fontWeight: 600 }}>
                  First In: {biometricLogs.in} &nbsp;|&nbsp; Last Out: {biometricLogs.out}
                </Typography>
                
                <FormLabel component="legend" sx={{ fontSize: '0.85rem', color: '#333', fontWeight: 'bold' }}>
                  Do you need to request this biometric time for swipe request?
                </FormLabel>
                <RadioGroup row value={biometricUse} onChange={(e) => setBiometricUse(e.target.value)}>
                  <FormControlLabel value="yes" control={<Radio size="small" />} label="Yes" />
                  <FormControlLabel value="no" control={<Radio size="small" />} label="No" />
                </RadioGroup>
              </>
            )}
          </Box>
        )}

        {/* NON-BIOMETRIC FLOW */}
        {!limitReached && ['WFH', 'Tour', 'Training', 'Meeting', 'Other'].includes(reason) && (
          <>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField 
                size="small" type="time" label="Check In Time" required 
                InputLabelProps={{ shrink: true }} 
                value={inTime} onChange={e => setInTime(e.target.value)} 
                sx={{ flex: 1 }}
              />
              <TextField 
                size="small" type="time" label="Check Out Time" required 
                InputLabelProps={{ shrink: true }} 
                value={outTime} onChange={e => setOutTime(e.target.value)} 
                sx={{ flex: 1 }}
              />
            </Box>

            {reason === 'Other' && (
              <TextField 
                size="small" label="Reason (Max 200 chars)" required multiline rows={2}
                inputProps={{ maxLength: 200 }}
                value={otherReason} onChange={e => setOtherReason(e.target.value)} 
              />
            )}

            <Box sx={{ border: '1px dashed #bdbdbd', p: 2, borderRadius: 1, textAlign: 'center' }}>
              <Button variant="outlined" component="label">
                Attach Proof Document *
                <input type="file" hidden required onChange={e => setFile(e.target.files[0])} />
              </Button>
              {file && <Typography variant="caption" display="block" sx={{ mt: 1, color: 'success.main', fontWeight: 'bold' }}>Attached: {file.name}</Typography>}
            </Box>
          </>
        )}

        <Button 
          type="submit" variant="contained" size="large" disableElevation 
          disabled={
            submitting || 
            !reason || 
            !date || 
            limitReached ||
            (reason === 'Biometric' && (biometricStatus === 'empty' || biometricUse === 'no'))
          }
        >
          {submitting ? 'Submitting...' : 'Submit Request'}
        </Button>
      </Box>
    </Paper>
  );
};

export default SwipeRequestSection;